/* ============================================================
   FLOW ULTRA — tab admin (Kelola Sistem)
   ============================================================
   Struktur: Parent account → Family sharing account → Tenant.
   Reka bentuk mengikut app.cal.com/apps/installed, guna token
   Sesaat Studio v3.9 (src/design/tokens.css + ui.css).

   ⚠️ DATA MASIH CONTOH. Koleksi flow_* belum wujud dalam Firestore
   dan rules belum dibuka, jadi skrin ini membaca SAMPLE_PARENTS di
   bawah. Bila backend siap, tukar SAMPLE_PARENTS kepada langganan
   Firestore — bentuk objeknya sengaja dikekalkan sama.
   Rujukan: PLAN_FLOW_ULTRA.md · PLAN_FLOW_ULTRA_WORKFLOW.md
   ============================================================ */

import { useEffect, useState } from 'react';
import {
    Mail, MoreVertical, Plus, LayoutGrid, KeyRound, X, Users, Send, Check, ChevronRight,
    Calendar, Layers, ChevronLeft, Search, Package, Receipt,
} from 'lucide-react';

import '../design/tokens.css';
import '../design/ui.css';
import './FlowUltraTab.css';
import FlowSessionPilotTab from './FlowSessionPilotTab';
import FlowExtendDaysModal from './FlowExtendDaysModal';
import FlowCustomerEmailField from './FlowCustomerEmailField';
import FlowSubscriptionModal from './FlowSubscriptionModal';
import FlowProductsScreen from './FlowProductsScreen';
import FlowOrdersScreen from './FlowOrdersScreen';
import FlowHostedJobsTab from './FlowHostedJobsTab';
import { Badge, Btn, Card, CardBody, CardFoot, Field, IconBtn } from '../design/ui';
import { muatDataFlow } from '../services/flowUltraService';
import { tetapAksesJenis2 } from '../services/flowT2Admin';
import { muatProdukFlow, produkUntuk } from '../services/flowProdukService';
import {
    muatRingkasanLangganan, gabungRingkasan, tambahLanggananManual, tandaDiingatkanLangganan, langgananLain, jenisRekod, cariAkaunSesaat,
} from '../services/flowLanggananService';
import { semakHubungan, pecahHubungan, ayatPadanan, EMEL_SAH } from '../utils/flowHubungan';
import { buildFlowDeeplink } from '../utils/whatsappTemplate';

/* ─────────── Pautan jualan ───────────
   SATU pautan buat semua kerja:
     · logged out → kod ejen `FLOW-DIRECT` ditangkap → butang Daftar terbuka
       (Login.jsx melumpuhkan Daftar bila medan kod kosong) → selepas daftar
       pelanggan dibawa BALIK ke /flow, bukan ke StartGate RM199.
     · logged in  → terus ke halaman bayar.
   `FLOW-DIRECT` sengaja BUKAN akaun ejen sebenar. Ia penanda sumber sahaja:
   ia disimpan sebagai users.referredBy, dan kerana tiada dokumen affiliates
   yang sepadan, tiada komisen hantu dikreditkan. */
const FLOW_SELL_LINK = 'https://app.sesaat.ai/flow?ref=FLOW-DIRECT';

/* ─────────── DATA SEBENAR ───────────
   DATA CONTOH SUDAH DIBUANG (26 Ogos 2026). Skrin ini kini membaca Firestore
   melalui services/flowUltraService.js.

   ⚠️ Dibuang SEPENUHNYA dan bukan disimpan sebagai sandaran, atas sebab yang
   penting: pelanggan dalam senarai ini SEBENAR, tetapi akaun family sharing
   belum diimport daripada Google Sheet. Kalau akaun contoh dibiarkan hidup di
   sebelah pelanggan sebenar, admin boleh memberikan slot yang tidak wujud
   kepada orang yang betul-betul sudah membayar. Skrin kosong lebih selamat
   daripada skrin yang menipu.

   TIGA TAHAP seorang pelanggan, setiap satu kerja berbeza:
     unassigned — sudah bayar, belum ada slot        → cari slot untuk dia
     ready      — ada slot, kredential belum dihantar → WhatsApp dia
     active     — hidup                              → pantau tarikh tamat
   Tahap dikira dalam flowUltraService, bukan di sini — satu takrifan sahaja. */

const STAGE = {
    unassigned: { label: 'Needs a Gmail', tone: 'stop', dot: 's-dot--stop' },
    ready: { label: 'Ready to send', tone: 'warn', dot: 's-dot--warn' },
    expired: { label: 'Expired', tone: 'stop', dot: 's-dot--stop' },
    expiring: { label: 'Expiring', tone: 'warn', dot: 's-dot--warn' },
    active: { label: 'Active', tone: 'neutral', dot: 's-dot--ok' },
};

/* ─────────── Pembantu ─────────── */
const rm = (n) => `RM${n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rm0 = (n) => `RM${n.toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
const isTenant = (x) => x && x !== 'rotate';
const tenantsOf = (a) => a.t.filter(isTenant);
const revOf = (a) => (a.priv ? 300 * Math.min(tenantsOf(a).length, 1) : 100 * Math.min(tenantsOf(a).length, a.cap));
const parentRev = (p) => p.accounts.reduce((s, a) => s + revOf(a), 0);
const parentTenants = (p) => p.accounts.reduce((s, a) => s + tenantsOf(a).length, 0);
const parentFree = (p) => p.accounts.reduce((s, a) => s + a.t.filter((x) => x === null).length, 0);
const shortMail = (e) => e.replace('@gmail.com', '');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
/* Tarikh sebenar di sebelah "N days left" — admin perlu menyebut tarikh kepada
   pelanggan, bukan bilangan hari. Waktu Malaysia, sama seperti endOfDayMY server. */
const tarikhMY = (d, hari = false) => {
    const t = d instanceof Date ? d : (d ? new Date(d) : null);
    if (!t || Number.isNaN(t.getTime())) return '';
    return t.toLocaleDateString('en-MY', {
        ...(hari ? { weekday: 'short' } : {}), day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur',
    });
};

/* Cari akaun + slot bagi seorang pelanggan, supaya skrin boleh memapar "dia
   duduk di mana". Pulangan null bermakna dia belum diberikan slot — itu keadaan
   biasa bagi setiap pembeli baharu, bukan ralat. */
function lokasiPelanggan(parents, c) {
    if (!c.accountId) return null;
    for (const p of parents) {
        for (const a of p.accounts) {
            if (a.id !== c.accountId) continue;
            const si = a.t.findIndex((x) => x && x !== 'rotate' && x.id === c.id);
            return { parent: p, acc: a, si: si >= 0 ? si : (c.slotIdx ?? 0) };
        }
    }
    return null;
}

const NEEDS_WORK = ['unassigned', 'ready', 'expired', 'expiring'];

/* Semua akaun family sharing merentas SEMUA blok, diratakan jadi satu senarai.
   Skrin dua panel perlu senarai ini kerana admin memikirkan "akaun mana", bukan
   "blok mana akaun itu duduk" — blok hanya penting bila bil Google tiba. */
function allAccounts(parents) {
    const out = [];
    parents.forEach((p, pi) => p.accounts.forEach((a, ai) => {
        if (a.email) out.push({ acc: a, parent: p, pi, ai });
    }));
    return out;
}

/* ─────────── Kalendar ───────────
   Tarikh tamat dikira daripada baki hari setiap penyewa, dan tarikh sambung
   dikira daripada kitaran 30 hari yang SAMA (tamat tolak 30). Kedua-duanya
   berpunca daripada satu nombor — jadi kalendar tidak boleh bercanggah dengan
   jadual penyewa, walau berapa kali data berubah. */
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function buildCalendar(subs, lokasiTeks) {
    const map = {};
    const push = (date, kind, row) => {
        if (!date) return;
        const k = dayKey(date);
        if (!map[k]) map[k] = { expire: 0, expireRM: 0, renew: 0, renewRM: 0, rows: [] };
        map[k][kind] += 1;
        map[k][kind === 'expire' ? 'expireRM' : 'renewRM'] += row.price;
        map[k].rows.push({ ...row, kind });
    };
    subs.forEach((s) => {
        const row = {
            phone: s.phone || s.email,
            acc: lokasiTeks(s),
            price: (s.priceSen || 0) / 100,
            days: s.days,
        };
        /* Dua tarikh daripada rekod yang SAMA — jadi kalendar mustahil
           bercanggah dengan jadual penyewa, walau berapa kali data berubah.
           Hijau = duit masuk (tarikh bayar), amber = duit berisiko (tamat). */
        push(s.expiresAt, 'expire', row);
        push(s.startedAt, 'renew', row);
    });
    return map;
}

/* Petak bulan, minggu bermula ISNIN. Baris terakhir yang langsung kosong
   dipotong — kalendar yang sentiasa 6 baris meninggalkan jalur kosong yang
   nampak seperti kerosakan susun atur. */
function monthGrid(base) {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const total = Math.ceil((lead + days) / 7) * 7;
    return Array.from({ length: total }, (_, i) =>
        new Date(base.getFullYear(), base.getMonth(), 1 - lead + i));
}

/* ─────────── Sub-komponen ─────────── */
function Stat({ label, value, sub }) {
    return (
        <Card>
            <div className="s-stat">
                <p className="s-stat__lab">{label}</p>
                <p className="s-stat__val">{value}</p>
                <p className="s-stat__sub">{sub}</p>
            </div>
        </Card>
    );
}

/* TenantRow (senarai baris penyewa dalam kad akaun) DIBUANG — digantikan oleh
   cip slot `.fu-slot`. Baris lama menjawab "bila dia tamat", tetapi soalan itu
   kini dijawab dengan lebih baik oleh jadual penyewa pada skrin Customers. Skrin
   Akaun tinggal SATU tugas: tunjuk bilik mana ada ruang untuk dijual — dan empat
   cip sebaris menjawabnya dalam satu pandangan, empat baris tidak. Amaran yang
   dibawa baris lama kekal, cuma sebagai warna cip + tooltip. */

function AccountCard({ account, parentIdx, accIdx, onCredentials, onRotate, onSlot, onToast }) {
    const n = tenantsOf(account).length;
    const empty = !account.email;
    const free = account.t.filter((x) => x === null).length;
    const needsRotate = account.t.some((x) => x === 'rotate');

    /* Bingkai merah bila akaun terlebih isi. Lencana kecil sahaja tidak memadai:
       akaun 5-dalam-4 bermakna seseorang membayar untuk tempat yang tidak wujud,
       dan itu mesti kelihatan dari hujung skrin. */
    return (
        <Card style={empty ? { borderStyle: 'dashed' }
            : n > account.cap ? { borderColor: 'var(--s-stop)' } : undefined}>
            <div className="s-row s-card__head">
                <span className="s-ic"><Mail size={15} strokeWidth={1.9} /></span>
                <div className="s-row__main">
                    {empty ? (
                        <>
                            <p className="s-row__name" style={{ color: 'var(--s-brand)' }}>Add a family sharing account</p>
                            <p className="s-row__sub">Free seat — 4 slots waiting</p>
                        </>
                    ) : (
                        <>
                            <p className="s-row__name">
                                {shortMail(account.email)}
                                {account.owner && <span className="s-dim" style={{ fontWeight: 400 }}> parent</span>}
                            </p>
                            <p className="s-row__sub">{n} of {account.cap} tenants · {rm0(revOf(account))}/month</p>
                        </>
                    )}
                </div>
                <div className="s-row__right">
                    {account.priv && <Badge tone="brand">Private</Badge>}
                    {n > account.cap && <Badge tone="warn">{n - account.cap} over</Badge>}
                    {needsRotate && <Badge tone="stop">Password change</Badge>}
                    {free > 0 && <Badge tone="neutral">{free} empty</Badge>}
                    {!empty && (
                        <>
                            <Btn variant="ghost" size="sm" onClick={() => onCredentials(account)}>Credentials</Btn>
                            <IconBtn label="Account menu" icon={<MoreVertical size={15} />}
                                onClick={() => (needsRotate ? onRotate(account) : onToast('Account menu is not wired yet.'))} />
                        </>
                    )}
                </div>
            </div>

            {!empty && (
                <div className="fu-slots">
                    {account.t.map((item, i) => {
                        if (i >= account.cap && item == null) return null;

                        let tone = '';
                        let label;
                        let title;

                        if (item === null) {
                            tone = ' fu-slot--free';
                            label = 'Empty';
                            title = `Slot ${i + 1} — empty, ready to sell`;
                        } else if (item === 'rotate') {
                            tone = ' fu-slot--stop';
                            label = 'Released';
                            title = `Slot ${i + 1} — released. The former tenant can still sign in until the password is changed.`;
                        } else {
                            /* Empat digit terakhir sahaja. Nombor penuh tidak muat dalam cip
                               selebar seperempat kad, dan empat digit itu sudah cukup untuk
                               admin mengenal siapa — nombor penuh ada dalam tooltip + jadual. */
                            label = `…${item.p.slice(-4)}`;
                            title = item.d < 0
                                ? `${item.p} — ${plural(Math.abs(item.d), 'day')} overdue`
                                : `${item.p} — ${plural(item.d, 'day')} left`;
                            if (item.d < 0) tone = ' fu-slot--stop';
                            else if (item.d <= 7) tone = ' fu-slot--warn';
                        }

                        return (
                            <button key={i} type="button" className={`fu-slot${tone}`} title={title}
                                onClick={() => onSlot({ parentIdx, accIdx, slotIdx: i })}>
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

/* ─────────── Komponen utama ─────────── */
export default function FlowUltraTab({ canManageSessions = false, initialSection = 'customers' }) {
    const [data, setData] = useState(null);
    const [ralat, setRalat] = useState('');

    /* Satu bacaan, sekali sahaja masa tab dibuka. Bukan langganan langsung
       (onSnapshot) kerana data ini bergerak beberapa kali sehari, bukan
       beberapa kali seminit — dan tab admin yang mendengar sepanjang hari
       membakar bacaan tanpa memberi apa-apa. Butang Muat semula ada di kepala. */
    /* `pusingan` dinaikkan selepas sebarang tulisan supaya senarai dibaca semula.
       Lebih mudah difahami daripada menyelit baris baharu ke dalam state secara
       tangan — dan ia mustahil terpesong daripada apa yang sebenarnya tersimpan. */
    const [pusingan, setPusingan] = useState(0);
    const muatSemula = () => setPusingan((n) => n + 1);

    useEffect(() => {
        let batal = false;
        muatDataFlow()
            .then((d) => { if (!batal) setData(d); })
            .catch((e) => { if (!batal) setRalat(e?.message || 'Could not read Flow Ultra data.'); });
        return () => { batal = true; };
    }, [pusingan]);

    /* PLAN_FLOW_PRODUK.md Fasa 0–1: produk + Gmail (Session Access / Type 2) bagi
       setiap langganan. Dimuat berasingan — kalau gagal (cth server belum deploy),
       senarai tetap keluar dan jalur merah menyatakan sebabnya, bukan senyap. */
    const [produk, setProduk] = useState([]);
    const [produkRalat, setProdukRalat] = useState('');
    const [ringkasan, setRingkasan] = useState(null);
    const [ringkasanRalat, setRingkasanRalat] = useState('');
    useEffect(() => {
        let batal = false;
        muatProdukFlow()
            .then((x) => { if (!batal) { setProduk(x); setProdukRalat(''); } })
            .catch((e) => { if (!batal) setProdukRalat(e?.code === 'permission-denied' ? 'The database rules for products are not deployed yet.' : (e?.message || 'unknown error')); });
        muatRingkasanLangganan()
            .then((x) => { if (!batal) { setRingkasan(x); setRingkasanRalat(''); } })
            .catch((e) => { if (!batal) setRingkasanRalat(e?.code === 'functions/not-found' ? 'the server update is not deployed yet' : (e?.message || 'unknown error')); });
        return () => { batal = true; };
    }, [pusingan]);

    const parents = data?.parents || [];

    /* Skrin lalai ialah CUSTOMERS, bukan Overview. Sebabnya bukan citarasa:
       hampir setiap kali tab ini dibuka, kerjanya ialah mengejar orang —
       hantar akses, kejar renewal. Overview ialah tempat menyemak duit, dan
       itu kerja mingguan. Nak patah balik: tukar 'customers' → 'overview'. */
    const [sel, setSel] = useState(initialSection === 'session-access' && canManageSessions ? 'session-access' : 'customers');
    const [credFor, setCredFor] = useState(null);
    const [credUnlocked, setCredUnlocked] = useState(false);
    const [revealed, setRevealed] = useState({});
    const [rotateFor, setRotateFor] = useState(null);
    const [rotateChecks, setRotateChecks] = useState([false, false, false, false, false]);
    const [rotatePw, setRotatePw] = useState('');
    const [slotRef, setSlotRef] = useState(null);
    const [toastMsg, setToastMsg] = useState('');
    const [custFilter, setCustFilter] = useState('todo');
    /* Carian = corak tab Users / Rekod Jualan: taip → Enter → term komited. */
    const [custCariInput, setCustCariInput] = useState('');
    const [custCari, setCustCari] = useState('');
    const [extendBuka, setExtendBuka] = useState(false);
    // Pelanggan yang tarikh tamatnya sedang diubah (butang "Change" dalam popup) — mod Set date.
    const [tarikhFor, setTarikhFor] = useState(null);
    /* Susunan jadual. Lalai 'latest' (keputusan user 27 Sept) — tidak disimpan, refresh = Latest. */
    const [custSort, setCustSort] = useState('latest');
    const [custFor, setCustFor] = useState(null);
    /* Tanda pilih SENGAJA tidak dikosongkan bila tapisan bertukar — admin
       menanda beberapa orang dalam "Expiring", tukar ke "Expired", tanda
       beberapa lagi, kemudian hantar sekali. Mengosongkannya setiap kali
       tapisan bertukar memaksa dia menghantar dua kali. */
    const [picked, setPicked] = useState(() => new Set());
    const [calOffset, setCalOffset] = useState(0);
    const [calDay, setCalDay] = useState(null);
    const [acctSel, setAcctSel] = useState(0);

    const customers = gabungRingkasan(data?.subs || [], ringkasan);

    /* Di mana pelanggan ini duduk, dalam ayat. Sehingga Google Sheet diimport,
       jawapannya "No account yet" untuk semua orang — dan itu memang keadaan
       sebenar, bukan data yang hilang. */
    const lokasi = (c) => lokasiPelanggan(parents, c);
    const teksLokasi = (c) => {
        if (c.gmail?.kind === 'session') return shortMail(c.gmail.gmail || c.gmail.label || c.gmail.accountId);
        if (c.gmail?.kind === 't2') return shortMail(c.gmail.gmail);
        const L = lokasi(c);
        if (L) return `${shortMail(L.acc.email)} · slot ${L.si + 1}`;
        /* Penyewa yang dimasukkan tangan membawa nama akaun sebagai teks bebas —
           itulah satu-satunya maklumat yang ada sebelum Sheet diimport. */
        if (c.accountText) return shortMail(c.accountText);
        return 'No account yet';
    };

    /* ── WhatsApp ──
       Butang ini BETUL-BETUL membuka WhatsApp dengan mesej siap tulis. Ia guna
       semula pembina deeplink yang sudah dipakai untuk renewal lesen SESAAT,
       jadi nada mesej Flow dan mesej lesen adalah sama.
       ⛔ Kata laluan tidak pernah masuk ke dalam pautan — lihat nota panjang
          dalam utils/whatsappTemplate.js. */
    const tahapMesej = (c) => {
        if (c.stage === 'unassigned' || c.stage === 'ready') return 'access';
        if (c.days != null && c.days < 0) return 'expired';
        if (c.days != null && c.days <= 1) return 'final';
        return 'remind';
    };

    const pautanWa = (c) => buildFlowDeeplink({
        stage: tahapMesej(c),
        name: c.name,
        email: c.email,
        phone: c.phone,
        expiresAt: c.expiresAt,
        daysLeft: c.days ?? 0,
        waitedDays: c.stage === 'unassigned' ? c.paidAgo : 0,
        extendedDays: 0,
    });

    /* ── Borang tambah pelanggan ──
       Untuk penyewa yang membayar DI LUAR sistem (transfer terus, jualan lama
       dalam Google Sheet). Medan sengaja sama dengan lajur templat CSV, supaya
       apa yang ditaip tangan hari ini dan apa yang diimport kemudian membawa
       maklumat yang serupa — tiada dua bentuk data untuk benda yang sama. */
    const BORANG_KOSONG = {
        hubungan: '', name: '', accountEmail: '',
        tamat: '', harga: '100', hantar: false, note: '',
        jenis: '1', mula: '', bayar: '', had: '4500', produkId: '',
    };
    const [tambahBuka, setTambahBuka] = useState(false);
    const [borang, setBorang] = useState(BORANG_KOSONG);
    const [borangRalat, setBorangRalat] = useState('');
    const [sedangSimpan, setSedangSimpan] = useState(false);
    /* Customer sudah ada langganan produk yang sama → server minta pengesahan dahulu
       (banyak langganan dibenarkan; ini hanya elak tertekan Add dua kali). */
    const [sahPendua, setSahPendua] = useState(false);
    /* Add customer menjumpai langganan produk yang sama → "Record as a renewal instead" (2026-10-14,
       arahan user — kes 601158883878: tamat semalam, bayar transfer hari ini, Add ditolak). Membuka
       popup Extend days mod 'paid' pada langganan SEDIA ADA (Gmail + projek kekal), RM + tarikh dari borang. */
    const [renewDari, setRenewDari] = useState(null); // { customers: [c], bayaranAwal: { rm, tarikh } }
    const bukaRenewSedia = () => {
        const h = semakHubungan(pecahHubungan(borang.hubungan));
        const jenis = borang.jenis === '2' ? 2 : 1;
        // Orang yang sama (uid / email / nombor) + produk yang sama — langganan terbaharu didahulukan.
        const sama = langgananLain({ id: '__borang', kind: 'sub', phone: h.phone, email: h.email, uid: null }, customers)
            .filter((x) => (borang.produkId ? produkUntuk(x, produk)?.id === borang.produkId : (x.jenis === 2 ? 2 : 1) === jenis))
            .sort((a, b) => (b.expiresAt?.getTime?.() || 0) - (a.expiresAt?.getTime?.() || 0));
        if (!sama.length) { setBorangRalat('Could not find that subscription in the list — press Refresh and try again.'); return; }
        setTambahBuka(false);
        setRenewDari({ customers: [sama[0]], bayaranAwal: { rm: borang.harga, tarikh: borang.bayar } });
    };

    const ubahBorang = (k, v) => { setSahPendua(false); setBorang((b) => ({ ...b, [k]: v })); };
    const isoHari = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const produkAktif = produk.filter((x) => x.active !== false);
    /* Pilih produk → type, harga, had credit dan tarikh tamat (mula + tempoh produk) terisi
       sendiri. Semuanya masih boleh ditukar (harga kawan, tarikh family renew Type 2). */
    const isiDariProduk = (b, id) => {
        const x = produkAktif.find((q) => q.id === id);
        if (!x) return { ...b, produkId: '' };
        const b0 = String(b.mula || '').split('-').map(Number);
        const mula0 = b0.length === 3 ? new Date(b0[0], b0[1] - 1, b0[2]) : new Date();
        const t = new Date(mula0); t.setDate(t.getDate() + x.days);
        return { ...b, produkId: x.id, jenis: String(x.jenis), harga: String(x.priceSen / 100),
            had: x.jenis === 2 && x.hadKredit ? String(x.hadKredit) : b.had, tamat: isoHari(t) };
    };

    const bukaBorang = () => {
        /* Lalai tarikh tamat = 30 hari dari hari ini. Kebanyakan entri manual
           ialah orang yang baru sahaja membayar, jadi ini menjimatkan satu
           langkah — dan ia tetap boleh ditukar. */
        const t = new Date();
        t.setDate(t.getDate() + 30);
        const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const hariIni = isoOf(new Date());
        const asas = { ...BORANG_KOSONG, tamat: isoOf(t), mula: hariIni, bayar: hariIni };
        const lalai = produkAktif.find((x) => x.jenis === 1) || produkAktif[0];
        setBorang(lalai ? isiDariProduk(asas, lalai.id) : asas);
        setBorangRalat('');
        setSahPendua(false);
        setTambahBuka(true);
    };

    const hantarBorang = async () => {
        setBorangRalat('');
        /* SATU kotak: nombor WhatsApp ATAU email akaun SESAAT (keputusan user 3 Okt + 10 Okt 2026) —
           peraturan + ayat dalam utils/flowHubungan.js; server menyemak sekali lagi. */
        const hubungan = semakHubungan(pecahHubungan(borang.hubungan));
        if (hubungan.ralat) { setBorangRalat(hubungan.ralat); return; }
        const digit = hubungan.phone;
        const tarikh = (s) => { const b = String(s || '').split('-').map(Number); const d = b.length === 3 ? new Date(b[0], b[1] - 1, b[2]) : null; return d && !Number.isNaN(d.getTime()) ? d : null; };
        const tamat = tarikh(borang.tamat), mula = tarikh(borang.mula), bayar = tarikh(borang.bayar);
        if (!tamat) { setBorangRalat('Pick an expiry date.'); return; }
        if (!mula) { setBorangRalat('Pick the subscription start date.'); return; }
        if (!bayar) { setBorangRalat('Pick the payment date.'); return; }
        if (tamat <= mula) { setBorangRalat('Expiry must be after the start date.'); return; }
        const jenis = borang.jenis === '2' ? 2 : 1;
        const had = Math.round(Number(borang.had));
        const harga = Math.round(Number(String(borang.harga).replace(/[^\d.]/g, '')) * 100);
        if (String(borang.harga).trim() === '' || !Number.isSafeInteger(harga) || harga < 0 || harga > 1000000) { setBorangRalat('Enter the price paid (RM0–RM10,000).'); return; }
        if (jenis === 2) {
            if (!EMEL_SAH.test(borang.accountEmail.trim())) { setBorangRalat('Type 2 needs the Google account (Gmail) given to this customer.'); return; }
            if (!Number.isSafeInteger(had) || had <= 0) { setBorangRalat('Enter a credit limit for type 2.'); return; }
        }

        setSedangSimpan(true);
        try {
            /* Type 2 mengikat Gmail kepada SATU akaun SESAAT. Email diisi → akaun itu. Nombor WhatsApp
               SAHAJA → server cari akaun ikut nombor; tiada / lebih dari satu akaun → minta email
               (belum ada apa-apa ditulis). Type 1 tidak perlukan langkah ini — padanannya dibuat
               server semasa menyimpan, dan hasilnya dilaporkan dalam toast di bawah. */
            let emelAkaun = hubungan.email;
            if (jenis === 2 && !emelAkaun) {
                const a = await cariAkaunSesaat({ phone: digit });
                if (!a?.found || !EMEL_SAH.test(a.email || '')) {
                    setBorangRalat(a?.via === 'phone-ambiguous'
                        ? 'More than one SESAAT account uses this WhatsApp number. Enter the customer\'s SESAAT account email instead.'
                        : 'No SESAAT account uses this WhatsApp number. Enter the customer\'s SESAAT account email instead.');
                    return;
                }
                emelAkaun = a.email;
            }
            /* ⛔ Type 2 kedua untuk akaun yang sama belum disokong — akses Gmail Type 2 satu bagi
               setiap akaun; langkah di bawah (tetapAksesJenis2) akan MENINDIH Gmail yang pertama.
               Disemak DI SINI sebelum akses disentuh; server menyemak sekali lagi. */
            if (jenis === 2 && customers.some((c) => c.jenis === 2 && c.email === emelAkaun && (c.days ?? 0) >= 0)) {
                setBorangRalat('This account already has an active Type 2 subscription. A second Type 2 for the same SESAAT account is not supported yet.'); return;
            }
            /* Jenis 2: ikat Gmail kepada akaun SESAAT DAHULU (server). Gagal → tiada rekod
               separuh; pelanggan tidak ditambah dan admin nampak sebabnya.
               ⚠️ KEMBAR: FlowTukarJenis2.jsx (tukar Jenis 1 → 2, bayaran sama) buat langkah sama. */
            let vaultId = null;
            if (jenis === 2) {
                ({ vaultId } = await tetapAksesJenis2({ email: emelAkaun, gmail: borang.accountEmail.trim().toLowerCase(), startedAt: mula, expiresAt: tamat, had }));
            }
            /* Fasa 3A: rekod ditulis oleh SERVER ke flow_subs (satu tempat simpan), dengan
               semakan nilai + pendua (customer + produk sama) di sana. */
            const hasil = await tambahLanggananManual({
                phone: digit, name: borang.name, email: emelAkaun,
                accountEmail: borang.accountEmail.trim().toLowerCase(),
                jenis, productId: borang.produkId || null,
                startedAt: mula.getTime(), expiresAt: tamat.getTime(), paidAt: bayar.getTime(),
                priceSen: harga, hadKredit: jenis === 2 ? had : null, note: borang.note,
                benarPendua: sahPendua,
            });
            setSahPendua(false);
            setTambahBuka(false);
            muatSemula();
            if (vaultId) toast(`Customer added. Store this Gmail's password + 2FA key in the vault as gmail-${vaultId}.`);
            else {
                /* Type 1: akaun SESAAT dijumpai atau belum — amaran kekal lebih lama supaya sempat dibaca. */
                const padan = ayatPadanan(hasil);
                toast(padan.teks, padan.amaran ? 12000 : 3500);
            }
        } catch (e) {
            /* Kegagalan paling mungkin ialah peraturan keselamatan belum
               dideploy. Katakan itu terus, jangan biar admin meneka. */
            if (e?.code === 'functions/already-exists') setSahPendua(true);
            setBorangRalat(e?.code === 'functions/not-found'
                ? 'Not saved — the server update is not deployed yet.'
                : (e?.message || 'Could not save.'));
        } finally {
            setSedangSimpan(false);
        }
    };

    const bukaWa = (c) => {
        const url = pautanWa(c);
        if (!url) { toast('This customer has no WhatsApp number.'); return; }
        window.open(url, '_blank', 'noopener');
        /* Fasa 3A: tanda melalui server — kini Billplz DAN manual (dahulu manual sahaja). */
        tandaDiingatkanLangganan(c)
            .then(muatSemula)
            .catch(() => toast('WhatsApp opened, but the reminder mark could not be saved.'));
    };
    const custCount = (f) => (f === 'all' ? customers.length
        : f === 'todo' ? customers.filter((c) => NEEDS_WORK.includes(c.stage)).length
            : customers.filter((c) => c.stage === f).length);
    /* Carian merentas SEMUA pelanggan, bukan dalam tapisan semasa — kalau tidak,
       pelanggan yang tidak 'Needs action' tidak akan dijumpai langsung.
       Nombor dibanding digit sahaja (+60 / sengkang / jarak diabaikan). */
    const cariLc = custCari.toLowerCase();
    const cariDigit = custCari.replace(/\D/g, '');
    const padanCari = (c) => {
        const teks = [c.name, c.email, c.accountText, lokasi(c)?.acc?.email]
            .filter(Boolean).join(' ').toLowerCase();
        if (teks.includes(cariLc)) return true;
        if (cariDigit.length >= 3) {
            const d = String(c.phone || '').replace(/\D/g, '');
            const tanpa6 = cariDigit.replace(/^6(?=0)/, '');
            return d.includes(cariDigit) || d.replace(/^6(?=0)/, '').includes(tanpa6);
        }
        return false;
    };
    const custTapis = custCari ? customers.filter(padanCari)
        : custFilter === 'all' ? customers
        : custFilter === 'todo' ? customers.filter((c) => NEEDS_WORK.includes(c.stage))
            : customers.filter((c) => c.stage === custFilter);
    /* "Latest"/"Oldest" = bila pelanggan MASUK sistem, bukan tarikh mula langganan:
       manual → tarikh ia ditambah (createdAt); Billplz → tarikh bayar terakhir
       (lastPaidAt → paidAt). Begini pelanggan yang baru ditambah / baru bayar naik
       ke atas walaupun tarikh mulanya diundurkan.
       "Subscription start" (masaMula) = tarikh mula langganan sebenar (startedAt).
       Tiada tarikh → paling bawah. */
    const masaBaru = (c) => (c.manual ? (c.createdAt || c.startedAt) : (c.paidAt || c.startedAt))?.getTime?.() || 0;
    const masaMula = (c) => c.startedAt?.getTime?.() || 0;
    const namaSusun = (c) => (c.name || c.email || c.phone || '').toLowerCase();
    const custList = [...custTapis].sort((a, b) => {
        if (custSort === 'oldest') return (masaBaru(a) || Infinity) - (masaBaru(b) || Infinity);
        if (custSort === 'started') return masaMula(b) - masaMula(a);
        if (custSort === 'expiring') return (a.days ?? 99999) - (b.days ?? 99999);
        if (custSort === 'name') return namaSusun(a).localeCompare(namaSusun(b));
        return masaBaru(b) - masaBaru(a);
    });

    const toast = (msg, tempoh = 3500) => {
        setToastMsg(msg);
        window.clearTimeout(toast._t);
        toast._t = window.setTimeout(() => setToastMsg(''), tempoh);
    };

    /* ⚠️ Hasil dikira daripada LANGGANAN, bukan daripada slot yang terisi.
       Sebabnya penting hari ini: kelapan-lapan pelanggan sudah membayar tetapi
       belum seorang pun diberikan slot. Kalau hasil dikira daripada slot, skrin
       akan melaporkan RM0 pada hari kau baru menerima RM800. */
    const totals = {
        rev: customers.filter((c) => c.status === 'active')
            .reduce((s, c) => s + (c.priceSen || 0) / 100, 0),
        cost: parents.reduce((s, p) => s + p.cost, 0),
        tenants: customers.filter((c) => c.accountId).length,
        free: parents.reduce((s, p) => s + parentFree(p), 0),
        cap: parents.reduce((s, p) => s + p.accounts.reduce((t, a) => t + a.cap, 0), 0),
    };

    const openCredentials = (account) => { setCredFor(account); setCredUnlocked(false); setRevealed({}); };
    const openRotate = (account) => { setRotateFor(account); setRotateChecks([false, false, false, false, false]); setRotatePw(''); };

    /* ── Rail kiri ── */
    const rail = (
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 0 }}>
            <button type="button" className={`s-navrow${sel === 'overview' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('overview')}>
                <span className="s-navrow__icon"><LayoutGrid size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Overview</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>Money &amp; actions</span>
                </span>
            </button>

            <button type="button" className={`s-navrow${sel === 'customers' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('customers')}>
                <span className="s-navrow__icon"><Users size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Subscriptions</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>
                        {plural(customers.length, 'subscription')}
                    </span>
                </span>
                {custCount('todo') > 0 && <Badge tone="warn">{custCount('todo')}</Badge>}
            </button>

            <button type="button" className={`s-navrow${sel === 'orders' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('orders')}>
                <span className="s-navrow__icon"><Receipt size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Orders</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>Every payment</span>
                </span>
            </button>

            <button type="button" className={`s-navrow${sel === 'products' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('products')}>
                <span className="s-navrow__icon"><Package size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Products</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>{plural(produk.length, 'product')}</span>
                </span>
            </button>

            <button type="button" className={`s-navrow${sel === 'calendar' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('calendar')}>
                <span className="s-navrow__icon"><Calendar size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Calendar</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>Expiries &amp; renewals</span>
                </span>
            </button>

            <button type="button" className={`s-navrow${sel === 'accounts' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('accounts')}>
                <span className="s-navrow__icon"><Layers size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block' }}>Accounts</span>
                    <span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>
                        Logins &amp; history
                    </span>
                </span>
            </button>

            {canManageSessions && <button type="button" className={`s-navrow${sel === 'session-access' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('session-access')}>
                <span className="s-navrow__icon"><KeyRound size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>Session Access<span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>Extension access &amp; health</span></span>
            </button>}
            {canManageSessions && <button type="button" className={`s-navrow${sel === 'hosted-jobs' ? ' s-navrow--on' : ''}`}
                style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel('hosted-jobs')}>
                <span className="s-navrow__icon"><Send size={16} strokeWidth={1.9} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>Hosted Jobs<span className="s-dim" style={{ display: 'block', fontSize: 11.5 }}>Central Worker queue</span></span>
            </button>}
            <div style={{ height: 1, background: 'var(--s-line)', margin: '9px 4px' }} />

            {parents.map((p, i) => {
                const need = p.accounts.reduce((s, a) =>
                    s + a.t.filter((x) => isTenant(x) && x.d <= 7).length + a.t.filter((x) => x === 'rotate').length, 0);
                return (
                    <button key={p.no} type="button" className={`s-navrow${sel === i ? ' s-navrow--on' : ''}`}
                        style={{ height: 'auto', padding: '7px 8px' }} onClick={() => setSel(i)}>
                        <span className="s-navrow__icon"><KeyRound size={16} strokeWidth={1.9} /></span>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {shortMail(p.email)}
                            </span>
                            <span className="s-dim" style={{ display: 'block', fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Block {p.no} · {plural(parentTenants(p), 'tenant')}
                            </span>
                        </span>
                        {p.late ? <Badge tone="stop">!</Badge> : (need > 0 ? <Badge tone="warn">{need}</Badge> : null)}
                    </button>
                );
            })}

            <div style={{ height: 1, background: 'var(--s-line)', margin: '9px 4px' }} />
            <button type="button" className="s-navrow" onClick={() => toast('Add parent form is not wired yet.')}>
                <span className="s-navrow__icon"><Plus size={16} strokeWidth={1.9} /></span>Add parent
            </button>
        </nav>
    );

    /* ── Overview ── */
    const attention = [];
    parents.forEach((p, pi) => p.accounts.forEach((a, ai) => a.t.forEach((x, si) => {
        if (isTenant(x) && x.d <= 7) attention.push({ x, p, a, pi, ai, si });
    })));
    attention.sort((u, v) => u.x.d - v.x.d);

    const overview = (
        <>
            <div className="s-sec"><div>
                <h2>Overview</h2>
                <p>This month&apos;s money, and what needs your attention.</p>
            </div></div>

            <div style={{ marginBottom: 22 }}>
                <Card>
                    <div className="s-card__head" style={{ padding: '16px 20px' }}>
                        <p className="s-cardname">Selling link</p>
                        <p className="s-dim" style={{ margin: '3px 0 0' }}>
                            Send this to a customer on WhatsApp. One link covers sign-up and payment.
                        </p>
                    </div>
                    <CardBody className="s-card__body--solid" style={{ borderTop: '1px solid var(--s-line)' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                            <div className="s-kv__box s-num" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--s-ink)' }}>
                                {FLOW_SELL_LINK}
                            </div>
                            <Btn variant="ghost" onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(FLOW_SELL_LINK);
                                    toast('Link copied.');
                                } catch {
                                    toast('Could not copy — select the link and copy manually.');
                                }
                            }}>Copy</Btn>
                        </div>
                        <p className="s-kv__hint">
                            RM100 for 30 days. New customers can register through this link even without an
                            agent code, and land back on the payment page afterwards.
                        </p>
                    </CardBody>
                </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 26 }}>
                <Stat label="Revenue this month" value={rm(totals.rev)} sub={plural(totals.tenants, 'active tenant')} />
                <Stat label="Google cost" value={rm(totals.cost)} sub={plural(parents.length, 'subscription')} />
                <Stat label="Profit" value={rm(totals.rev - totals.cost)} sub="Before gateway fees" />
                <Stat label="Empty slots" value={String(totals.free)} sub={`Worth ${rm0(totals.free * 100)}/month`} />
            </div>

            {/* ── Pelanggan menunggu — DIDAHULUKAN daripada tarikh tamat ──
                Sebabnya bukan kosmetik: orang yang sudah bayar tetapi belum terima
                akaunnya sedang menunggu SEKARANG. Tarikh tamat boleh tunggu esok. */}
            {custCount('unassigned') + custCount('ready') > 0 && (
                <>
                    <div className="s-sec">
                        <div>
                            <h2>Waiting for their account</h2>
                            <p>They paid. They still do not have their login.</p>
                        </div>
                        <Btn variant="ghost" size="sm" onClick={() => setSel('customers')}>Open customers</Btn>
                    </div>

                    <div style={{ marginBottom: 26 }}>
                        <Card>
                            {customers.filter((c) => c.stage === 'unassigned' || c.stage === 'ready')
                                .slice(0, 5)
                                .map((c) => (
                                    <div key={c.id} className="s-row s-row--click" onClick={() => { setSel('customers'); setCustFor(c); }}>
                                        <span className={`s-dot ${STAGE[c.stage].dot}`} />
                                        <div className="s-row__main">
                                            <p className="s-row__name s-num" style={{ fontSize: 13.5, color: 'var(--s-ink)' }}>
                                                {c.name || c.phone || c.email}
                                            </p>
                                            <p className="s-row__sub">
                                                {c.stage === 'unassigned'
                                                    ? `Waiting ${plural(c.paidAgo, 'day')} · no slot given yet`
                                                    : `${teksLokasi(c)} · slot given, details never sent`}
                                            </p>
                                        </div>
                                        <div className="s-row__right">
                                            <Badge tone={STAGE[c.stage].tone}>{STAGE[c.stage].label}</Badge>
                                        </div>
                                    </div>
                                ))}
                        </Card>
                    </div>
                </>
            )}

            <div className="s-sec">
                <div><h2>Needs attention</h2><p>Overdue, and everything expiring within 7 days.</p></div>
                <Btn variant="ghost" size="sm" onClick={() => toast('WhatsApp sending is not wired yet — see PLAN_MURPATI_WHATSAPP.md.')}>Remind all</Btn>
            </div>

            <Card>
                {attention.map((o, k) => (
                    <div key={k} className="s-row s-row--click"
                        onClick={() => setSlotRef({ parentIdx: o.pi, accIdx: o.ai, slotIdx: o.si })}>
                        <span className={`s-dot ${o.x.d < 0 ? 's-dot--stop' : 's-dot--warn'}`} />
                        <div className="s-row__main">
                            <p className="s-row__name s-num" style={{ fontSize: 13.5, color: 'var(--s-ink)' }}>{o.x.p}</p>
                            <p className="s-row__sub">{shortMail(o.a.email)} · block {o.p.no}</p>
                        </div>
                        <div className="s-row__right">
                            <Badge tone={o.x.d < 0 ? 'stop' : 'warn'}>
                                {o.x.d < 0 ? `${plural(Math.abs(o.x.d), 'day')} overdue` : `${plural(o.x.d, 'day')} left`}
                            </Badge>
                            <Btn variant="ghost" size="sm"
                                onClick={(e) => { e.stopPropagation(); toast('WhatsApp sending is not wired yet.'); }}>Remind</Btn>
                        </div>
                    </div>
                ))}

                {parents.map((p) => p.accounts.filter((a) => a.t.some((x) => x === 'rotate')).map((a) => (
                    <div key={`r${a.email}`} className="s-row">
                        <span className="s-dot s-dot--stop" />
                        <div className="s-row__main">
                            <p className="s-row__name">{shortMail(a.email)}</p>
                            <p className="s-row__sub">Slot released — the former tenant can still sign in</p>
                        </div>
                        <div className="s-row__right">
                            <Badge tone="stop">Password change</Badge>
                            <Btn size="sm" onClick={() => openRotate(a)}>Change</Btn>
                        </div>
                    </div>
                )))}

                {parents.filter((p) => p.late || p.chargeIn <= 7).map((p) => (
                    <div key={`c${p.no}`} className="s-row">
                        <span className={`s-dot ${p.late ? 's-dot--stop' : 's-dot--warn'}`} />
                        <div className="s-row__main">
                            <p className="s-row__name">Google charges {shortMail(p.email)}</p>
                            <p className="s-row__sub">{p.charge} · {rm(p.cost)} · card {p.card}</p>
                        </div>
                        <div className="s-row__right">
                            <Badge tone={p.late ? 'stop' : 'neutral'}>{p.late ? 'Overdue' : 'Due soon'}</Badge>
                        </div>
                    </div>
                ))}
            </Card>

            <div style={{ marginTop: 14 }}>
                <Card>
                    <div className="s-row">
                        <span className="s-dot s-dot--off" />
                        <div className="s-row__main">
                            <p className="s-row__name">{totals.free} empty slots out of {totals.cap} capacity</p>
                            <p className="s-row__sub">Your Google cost is the same whether a slot is filled or not — every slot you sell is pure margin.</p>
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );

    /* ── Pelanggan ──
       Satu skrin, satu senarai kerja. Tapisan lalai ialah "Needs action" kerana
       itulah soalan sebenar setiap pagi: siapa yang aku kena uruskan hari ini?
       "All" ada, tetapi ia bukan tempat kerja bermula. */
    /* ── Peti tugasan ──
       Menggantikan pil tapisan kecil. Setiap baris ialah satu keadaan pelanggan
       yang menuntut kerja BERBEZA, dan menekannya menapis jadual di bawah — ia
       kawalan sebenar, bukan papan tanda. Menekan baris yang sedang aktif sekali
       lagi memulangkan gabungan keempat-empatnya, jadi tiada jalan buntu.
       ⛔ Jangan pulangkan pil `fu-pills` di sebelahnya: dua kawalan yang menapis
       benda sama membuat admin tertanya-tanya sama ada ia dua benda berlainan. */
    const TASKS = [
        { id: 'unassigned', label: 'Needs a Gmail', dot: 's-dot--stop', sub: 'Paid, but no Gmail in Session Access yet' },
        { id: 'expiring', label: 'Expiring within 7 days', dot: 's-dot--warn', sub: 'Remind them before the slot lapses' },
        { id: 'expired', label: 'Expired', dot: 's-dot--stop', sub: 'Lapsed and not renewed yet' },
    ];

    const togglePick = (id) => setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    /* Tanda-semua hanya menyentuh baris yang SEDANG kelihatan, dan ia MENGGABUNG
       dengan pilihan sedia ada. Kalau ia mengganti, admin yang menanda dalam satu
       tapisan kemudian bertukar tapisan akan kehilangan pilihan pertamanya senyap. */
    const shownIds = custList.map((c) => c.id);
    const allShownPicked = shownIds.length > 0 && shownIds.every((id) => picked.has(id));
    const toggleAllShown = () => setPicked((prev) => {
        const next = new Set(prev);
        shownIds.forEach((id) => (allShownPicked ? next.delete(id) : next.add(id)));
        return next;
    });

    const timeLeft = (c) => (c.stage === 'unassigned' ? '—'
        : c.days < 0 ? `${plural(Math.abs(c.days), 'day')} overdue` : `${plural(c.days, 'day')} left`);

    const lastReminder = (c) => (c.stage === 'unassigned' || c.stage === 'ready' ? 'Never'
        : c.reminded ? 'Reminder sent' : 'Not sent yet');

    const custTableRow = (c) => {
        const st = STAGE[c.stage];
        const on = picked.has(c.id);
        const mula = tarikhMY(c.startedAt);
        const akhir = tarikhMY(c.expiresAt);
        return (
            <tr key={c.id} onClick={() => setCustFor(c)}>
                <td>
                    <button type="button" className={`fu-chk${on ? ' fu-chk--on' : ''}`}
                        aria-label={`Select ${c.name || c.phone || c.email}`} aria-pressed={on}
                        onClick={(e) => { e.stopPropagation(); togglePick(c.id); }}>
                        <Check size={11} strokeWidth={3} />
                    </button>
                </td>
                <td>
                    <span className="fu-tbl__who">{c.name || c.phone || c.email}</span>
                    {c.name && c.phone && <span className="s-num"> · {c.phone}</span>}
                    {(() => {
                        const n = langgananLain(c, customers).length;
                        return n > 0 ? <span className="s-dim" style={{ display: 'block', fontSize: 12 }}>+{n} other subscription{n === 1 ? '' : 's'}</span> : null;
                    })()}
                </td>
                <td>{produkUntuk(c, produk)?.name || `Type ${c.jenis === 2 ? 2 : 1}`}</td>
                <td>{teksLokasi(c)}</td>
                <td className="s-num">{mula || '—'}</td>
                <td className="s-num">
                    {akhir && <span style={{ display: 'block' }}>{akhir}</span>}
                    {(c.stage !== 'unassigned' || !akhir) && <span className={akhir ? 's-dim' : undefined}>{timeLeft(c)}</span>}
                </td>
                <td><Badge tone={st.tone}>{st.label}</Badge></td>
                <td>{lastReminder(c)}</td>
                <td style={{ textAlign: 'right' }}>
                    <Btn variant="ghost" size="sm" disabled={!c.phone}
                        onClick={(e) => { e.stopPropagation(); bukaWa(c); }}>
                        WhatsApp
                    </Btn>
                </td>
            </tr>
        );
    };

    const customersScreen = (
        <>
            <div className="s-sec">
                <div>
                    <h2>Subscriptions</h2>
                    <p>One row per subscription. A renewal extends the same row — every payment is listed in Orders.</p>
                </div>
                <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                    <Btn variant="ghost" size="sm" onClick={() => toast('Export is not wired yet.')}>Export</Btn>
                    <Btn size="sm" icon={<Plus size={14} />} onClick={bukaBorang}>Add customer</Btn>
                </div>
            </div>

            {/* Peti tugasan — soalan pertama setiap pagi: apa yang aku kena buat
                hari ini? Ia di ATAS kerana jawapannya lebih penting daripada
                senarai penuh, dan setiap baris terus menapis jadual di bawah. */}
            <div className="fu-tasks" style={custCari ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                {TASKS.map((t) => (
                    <button key={t.id} type="button"
                        className={`fu-task${custFilter === t.id ? ' fu-task--on' : ''}`}
                        aria-pressed={custFilter === t.id}
                        onClick={() => setCustFilter(custFilter === t.id ? 'todo' : t.id)}>
                        <span className={`s-dot ${t.dot}`} />
                        <span className="fu-task__lbl">
                            <b>{t.label}</b>
                            <span>{t.sub}</span>
                        </span>
                        <span className="fu-task__n">{custCount(t.id)}</span>
                        <span className="fu-task__arw"><ChevronRight size={16} strokeWidth={1.9} /></span>
                    </button>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 10px' }}>
                <form onSubmit={(e) => { e.preventDefault(); setCustCari(custCariInput.trim()); }}
                    style={{ flex: '1 1 320px', maxWidth: 420 }}>
                    <Field icon={<Search size={14} />} value={custCariInput}
                        onChange={(e) => setCustCariInput(e.target.value)}
                        placeholder="Search name, email, WhatsApp or family account…"
                        trailing={(custCariInput || custCari) && (
                            <button type="button" className="fu-linkbtn" aria-label="Clear search" title="Clear search"
                                onClick={() => { setCustCariInput(''); setCustCari(''); }}>
                                <X size={14} />
                            </button>
                        )} />
                </form>
                <select className="s-select" aria-label="Sort customers" style={{ width: 'auto', flex: 'none' }}
                    value={custSort} onChange={(e) => setCustSort(e.target.value)}>
                    <option value="latest">Sort: Latest</option>
                    <option value="oldest">Sort: Oldest</option>
                    <option value="started">Sort: Subscription start</option>
                    <option value="expiring">Sort: Expiring soonest</option>
                    <option value="name">Sort: Name A–Z</option>
                </select>
                <span className="s-dim">
                    Showing {plural(custList.length, 'customer')}
                    {custCari ? ` matching "${custCari}"`
                        : custFilter === 'todo' ? ' that need action'
                        : custFilter === 'all' ? ''
                            : ` · ${STAGE[custFilter].label}`}
                </span>
                {!custCari && <button type="button" className="fu-linkbtn"
                    onClick={() => setCustFilter(custFilter === 'all' ? 'todo' : 'all')}>
                    {custFilter === 'all'
                        ? `Back to what needs action (${custCount('todo')})`
                        : `Show all ${customers.length} customers`}
                </button>}
            </div>

            <div className="fu-tblwrap">
                <table className="fu-tbl">
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}>
                                <button type="button" className={`fu-chk${allShownPicked ? ' fu-chk--on' : ''}`}
                                    aria-label="Select everyone shown" aria-pressed={allShownPicked}
                                    onClick={toggleAllShown} disabled={shownIds.length === 0}>
                                    <Check size={11} strokeWidth={3} />
                                </button>
                            </th>
                            <th>Customer</th>
                            <th>Product</th>
                            <th>Google account</th>
                            <th>Starts</th>
                            <th>Ends</th>
                            <th>Status</th>
                            <th>Last reminder</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {custList.length === 0
                            ? <tr><td className="fu-tbl__none" colSpan={9}>{custCari ? `No customer matches "${custCari}".` : 'No customer is in this state right now.'}</td></tr>
                            : custList.map(custTableRow)}
                    </tbody>
                </table>
            </div>

            {/* Bar pukal — inilah sebab jadual ini wujud. Tanpa tanda pilih pukal,
                menghantar peringatan kepada 20 orang bermakna 20 kali kerja. */}
            {picked.size > 0 && (
                <div className="fu-bulk">
                    <span>{plural(picked.size, 'customer')} selected</span>
                    {/* ⚠️ Membuka SATU tab setiap kali ditekan, bukan semua serentak.
                        Browser menyekat pembukaan tab berganda, dan WhatsApp Web
                        hanya boleh memegang satu perbualan pada satu masa — jadi
                        "hantar 20 sekali gus" mustahil di sini. Ia membuka orang
                        pertama yang belum diingatkan, dan tandanya bergerak. */}
                    <button type="button" className="fu-bulk__b"
                        onClick={() => {
                            const senarai = custList.filter((c) => picked.has(c.id) && c.phone);
                            const seterusnya = senarai.find((c) => !c.reminded) || senarai[0];
                            if (!seterusnya) { toast('None of the selected customers has a WhatsApp number.'); return; }
                            bukaWa(seterusnya);
                        }}>
                        WhatsApp seterusnya
                    </button>
                    <button type="button" className="fu-bulk__b"
                        onClick={() => toast('Email sending is not wired yet.')}>Send email</button>
                    <button type="button" className="fu-bulk__b"
                        onClick={() => setExtendBuka(true)}>Extend days</button>
                    <button type="button" className="fu-bulk__x" onClick={() => setPicked(new Set())}>Clear</button>
                </div>
            )}

            <p className="s-kv__hint" style={{ marginTop: 10 }}>
                A customer appears here the moment their payment clears — you no longer have to
                remember who is waiting. <b>Last reminder</b> is what stops you sending the same
                person a second time.
            </p>
        </>
    );

    /* ── Kalendar tamat ──
       Menjawab soalan yang tiada skrin lain boleh jawab: bulan depan cukup duit
       atau tidak. Amber = duit yang akan hilang kalau tiada siapa mengejar;
       hijau = duit yang sudah masuk. */
    const today = new Date();
    const calMap = buildCalendar(customers, teksLokasi);
    const calBase = new Date(today.getFullYear(), today.getMonth() + calOffset, 1);
    const calCells = monthGrid(calBase);
    const todayKey = dayKey(today);
    const calPrefix = `${calBase.getFullYear()}-${String(calBase.getMonth() + 1).padStart(2, '0')}`;
    const keyToDate = (k) => { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d); };

    const next7 = customers
        .filter((c) => c.days != null && c.days >= 0 && c.days <= 7)
        .map((c) => (c.priceSen || 0) / 100);
    const monthRenewRM = Object.keys(calMap)
        .filter((k) => k.startsWith(calPrefix))
        .reduce((s, k) => s + calMap[k].renewRM, 0);

    const calendarScreen = (
        <>
            <div className="s-sec">
                <div>
                    <h2>Expiry calendar</h2>
                    <p>Who lapses on which day, and how much money is attached to it.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                    <IconBtn label="Previous month" icon={<ChevronLeft size={15} />}
                        onClick={() => { setCalOffset(calOffset - 1); setCalDay(null); }} />
                    <Btn variant="ghost" size="sm" onClick={() => { setCalOffset(0); setCalDay(null); }}>Today</Btn>
                    <IconBtn label="Next month" icon={<ChevronRight size={15} />}
                        onClick={() => { setCalOffset(calOffset + 1); setCalDay(null); }} />
                </div>
            </div>

            <div className="fu-strip">
                {/* Ditulis penuh, bukan melalui `plural()` — helper itu hanya
                    menambah "s", jadi ia menghasilkan "0 expirys". */}
                <span>Next 7 days · <b>{next7.length} expiring</b> · <b>{rm0(next7.reduce((s, n) => s + n, 0))}</b> at risk</span>
                <span>{calBase.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })} · <b>{rm0(monthRenewRM)}</b> received</span>
            </div>

            <div className="fu-cal" style={{ marginBottom: 6 }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                    <div key={d} className="fu-calhd">{d}</div>
                ))}
            </div>
            <div className="fu-cal">
                {calCells.map((d) => {
                    const k = dayKey(d);
                    const ev = calMap[k];
                    const inMonth = d.getMonth() === calBase.getMonth();
                    return (
                        <button key={k} type="button"
                            className={`fu-day${inMonth ? '' : ' fu-day--dim'}${k === todayKey ? ' fu-day--today' : ''}${calDay === k ? ' fu-day--on' : ''}`}
                            onClick={() => setCalDay(calDay === k ? null : k)}>
                            <span className="fu-day__d">{d.getDate()}</span>
                            {ev && ev.expire > 0 && (
                                <span className="fu-day__e" style={{ color: 'var(--s-warn)' }}>
                                    {ev.expire} expiring<br />{rm0(ev.expireRM)}
                                </span>
                            )}
                            {ev && ev.renew > 0 && (
                                <span className="fu-day__e" style={{ color: 'var(--s-ok)' }}>
                                    {ev.renew} paid<br />{rm0(ev.renewRM)}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {calDay && (
                <div style={{ marginTop: 16 }}>
                    <div className="s-sec">
                        <div>
                            <h2>{keyToDate(calDay).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</h2>
                            <p>{calMap[calDay] ? plural(calMap[calDay].rows.length, 'entry') : 'Nothing on this day.'}</p>
                        </div>
                        <Btn variant="ghost" size="sm" onClick={() => setCalDay(null)}>Close</Btn>
                    </div>
                    <Card>
                        {calMap[calDay] ? calMap[calDay].rows.map((r, i) => (
                            <div key={`${r.phone}-${r.kind}-${i}`} className="s-row">
                                <span className={`s-dot ${r.kind === 'expire' ? 's-dot--warn' : 's-dot--ok'}`} />
                                <div className="s-row__main">
                                    <p className="s-row__name s-num" style={{ fontSize: 13.5 }}>{r.phone}</p>
                                    <p className="s-row__sub">{r.acc}</p>
                                </div>
                                <div className="s-row__right">
                                    <Badge tone={r.kind === 'expire' ? 'warn' : 'neutral'}>
                                        {r.kind === 'expire' ? 'Expires' : 'Paid'}
                                    </Badge>
                                    <span className="s-dim s-num">{rm0(r.price)}</span>
                                </div>
                            </div>
                        )) : (
                            <div className="s-row">
                                <span className="s-dot s-dot--off" />
                                <div className="s-row__main"><p className="s-row__name">Nothing on this day</p></div>
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </>
    );

    /* ── Dua panel ──
       Satu-satunya skrin yang mengumpulkan kredential, penyewa dan sejarah satu
       akaun di tempat yang sama. Papan slot menjawab "bilik mana ada ruang";
       skrin ini menjawab "apa cerita bilik ini". */
    const flatAccounts = allAccounts(parents);
    const acctIdx = Math.min(acctSel, Math.max(0, flatAccounts.length - 1));
    const chosen = flatAccounts[acctIdx];

    /* ⚠️ `chosen` boleh jadi undefined — dan hari ini ia MEMANG undefined untuk
       semua orang, kerana `flow_accounts` belum diimport. Tanpa pagar ini
       seluruh tab putih (React melempar pada `chosen.acc.email`). */
    const accountsScreen = !chosen ? (
        <>
            <div className="s-sec">
                <div>
                    <h2>Accounts</h2>
                    <p>Every family sharing account in one list.</p>
                </div>
            </div>
            <Card>
                <div className="s-row">
                    <span className="s-dot s-dot--off" />
                    <div className="s-row__main">
                        <p className="s-row__name">No family sharing accounts yet</p>
                        <p className="s-row__sub">
                            Your Google accounts and their slots still live in the spreadsheet. Import it and
                            this screen fills itself — nothing here needs to be typed twice.
                        </p>
                    </div>
                </div>
            </Card>
        </>
    ) : (
        <>
            <div className="s-sec">
                <div>
                    <h2>Accounts</h2>
                    <p>Every family sharing account in one list. Pick one to see its login, tenants and history.</p>
                </div>
            </div>

            <div className="fu-split">
                <div className="fu-rail2">
                    {flatAccounts.map((f, i) => {
                        const n = tenantsOf(f.acc).length;
                        const free = f.acc.t.filter((x) => x === null).length;
                        return (
                            <button key={`${f.pi}-${f.ai}`} type="button"
                                className={`fu-rail2row${i === acctIdx ? ' fu-rail2row--on' : ''}`}
                                onClick={() => setAcctSel(i)}>
                                <div className="fu-rail2row__n">{shortMail(f.acc.email)}</div>
                                <div className="fu-rail2row__s">
                                    {n > f.acc.cap ? `${n} of ${f.acc.cap} · over capacity`
                                        : free ? `${plural(free, 'slot')} free` : 'Full'}
                                </div>
                                <div className="fu-bar">
                                    {Array.from({ length: Math.max(f.acc.cap, n) }, (_, s) => {
                                        const item = f.acc.t[s];
                                        let cls = 'fu-bar--free';
                                        if (item === 'rotate') cls = 'fu-bar--stop';
                                        else if (isTenant(item)) cls = item.d < 0 ? 'fu-bar--stop' : item.d <= 7 ? 'fu-bar--warn' : '';
                                        return <i key={s} className={cls} />;
                                    })}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <Card>
                    <div className="s-row s-card__head">
                        <span className="s-ic"><Mail size={15} strokeWidth={1.9} /></span>
                        <div className="s-row__main">
                            <p className="s-row__name">{chosen.acc.email}</p>
                            <p className="s-row__sub">
                                Block {chosen.parent.no} · {plural(tenantsOf(chosen.acc).length, 'tenant')} · {rm0(revOf(chosen.acc))}/month
                            </p>
                        </div>
                        <div className="s-row__right">
                            <Btn variant="ghost" size="sm" onClick={() => openRotate(chosen.acc)}>Change password</Btn>
                        </div>
                    </div>

                    <div style={{ padding: '16px 20px' }}>
                        {/* Kata laluan tidak pernah dipapar terus di sini. Menekan Show
                            membuka modal kredential yang sedia ada, yang ada pagarnya
                            sendiri — satu laluan sahaja untuk mendedahkan rahsia. */}
                        <div className="fu-cred">
                            <span className="s-dim">Password</span>
                            <span style={{ letterSpacing: '.16em', color: 'var(--s-ink-2)' }}>••••••••••••</span>
                            <span style={{ marginLeft: 'auto' }}>
                                <Btn variant="ghost" size="sm" onClick={() => openCredentials(chosen.acc)}>Show</Btn>
                            </span>
                        </div>

                        <div style={{ marginTop: 6 }}>
                            {chosen.acc.t.map((item, i) => {
                                if (i >= chosen.acc.cap && item == null) return null;
                                return (
                                    <div key={i} className="fu-dl">
                                        <span className="s-num" style={{ color: isTenant(item) ? 'var(--s-ink)' : undefined }}>
                                            {item === null ? `Slot ${i + 1} — empty`
                                                : item === 'rotate' ? `Slot ${i + 1} — released` : item.p}
                                        </span>
                                        <span className="s-num">
                                            {isTenant(item)
                                                ? (item.d < 0 ? `${plural(Math.abs(item.d), 'day')} overdue` : `${plural(item.d, 'day')} left`)
                                                : item === 'rotate' ? 'Old password still works' : 'Ready to sell'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="fu-log">
                            Activity log is sample text until this screen reads Firestore.<br />
                            Password last changed 14 Aug · {plural(tenantsOf(chosen.acc).length, 'tenant')} notified<br />
                            Last access details sent 25 Aug
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );

    /* ── Satu parent ── */
    const renderParent = (p, pi) => {
        const rev = parentRev(p);
        const profit = rev - p.cost;
        const t = parentTenants(p);
        const free = parentFree(p);
        const cap = p.accounts.reduce((s, a) => s + a.cap, 0);
        const over = Math.max(0, t - cap);
        // Jangan render "22 of 21 slots filled" — itu nampak macam bug.
        const fillLine = over
            ? `${plural(t, 'tenant')} · ${over} over capacity`
            : `${t} of ${cap} slots filled${free ? ` · ${free} empty` : ''}`;

        return (
            <>
                <div className="s-sec">
                    <div>
                        <h2>{p.email}</h2>
                        <p>Parent account · {plural(p.accounts.filter((a) => a.email).length, 'family sharing account')} · {plural(t, 'tenant')}</p>
                    </div>
                    <Btn size="sm" icon={<Plus size={14} />} onClick={() => toast('Add account form is not wired yet.')}>Add account</Btn>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <Card>
                        <div className="s-card__head" style={{ padding: '16px 20px' }}>
                            <p className="s-cardname">Subscription details</p>
                            <p className="s-dim" style={{ margin: '3px 0 0' }}>These values feed your profit calculation.</p>
                        </div>
                        <CardBody className="s-card__body--solid" style={{ borderTop: '1px solid var(--s-line)' }}>
                            <div className="s-kv">
                                <div>
                                    <p className="s-kv__lab">Google charge date</p>
                                    <div className="s-kv__box">
                                        {p.charge}
                                        {p.late
                                            ? <span style={{ marginLeft: 'auto' }}><Badge tone="stop">Overdue</Badge></span>
                                            : (p.chargeIn <= 7 ? <span style={{ marginLeft: 'auto' }}><Badge tone="warn">in {plural(p.chargeIn, 'day')}</Badge></span> : null)}
                                    </div>
                                    <p className="s-kv__hint">You get a reminder 7 days before.</p>
                                </div>
                                <div>
                                    <p className="s-kv__lab">Monthly cost</p>
                                    <div className="s-kv__box">{rm(p.cost)}</div>
                                    <p className="s-kv__hint">Card {p.card} · {p.promo}</p>
                                </div>
                                <div>
                                    <p className="s-kv__lab">Device</p>
                                    <div className="s-kv__box">{p.device}</div>
                                    <p className="s-kv__hint">The device holding the signed-in session.</p>
                                </div>
                                <div>
                                    <p className="s-kv__lab">Credits left</p>
                                    <div className="s-kv__box">{p.credits.toLocaleString('en-MY')}</div>
                                    <p className="s-kv__hint">Updated manually.</p>
                                </div>
                            </div>
                        </CardBody>
                        <CardFoot>
                            <span className="s-dim">{fillLine}</span>
                            <span className="s-dim">
                                Revenue <b style={{ color: 'var(--s-ok)' }}>{rm(rev)}</b>
                                {' · '}Profit <b style={{ color: profit < 0 ? 'var(--s-stop)' : 'var(--s-ink)' }}>{rm(profit)}</b>
                            </span>
                        </CardFoot>
                    </Card>
                </div>

                <div className="s-sec" style={{ marginTop: 22 }}>
                    <div>
                        <h2>Family sharing accounts</h2>
                        <p>Each account holds up to 4 tenants. Click a tenant for details.</p>
                    </div>
                </div>

                <div className="s-grid2">
                    {p.accounts.map((a, ai) => (
                        <AccountCard key={ai} account={a} parentIdx={pi} accIdx={ai}
                            onCredentials={openCredentials} onRotate={openRotate}
                            onSlot={setSlotRef} onToast={toast} />
                    ))}
                </div>
            </>
        );
    };

    /* ── Modal: kredential ── */
    const credRows = [
        { label: 'Email', value: credFor?.email || '' },
        { label: 'Password', value: 'Fl0w-x8k2qm+' },
        { label: '2FA key', value: 'abcd efgh ijkl mnop' },
    ];

    const slotData = slotRef ? parents[slotRef.parentIdx].accounts[slotRef.accIdx].t[slotRef.slotIdx] : undefined;
    const slotAcc = slotRef ? parents[slotRef.parentIdx].accounts[slotRef.accIdx] : null;
    const slotParent = slotRef ? parents[slotRef.parentIdx] : null;

    if (!data && !ralat && sel !== 'session-access' && sel !== 'hosted-jobs') {
        return (
            <div className="s-root" style={{ background: 'var(--s-panel)', border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-lg)', padding: '26px 28px 34px' }}>
                <h1 className="s-title">Flow Ultra</h1>
                <p className="s-subtitle" style={{ marginTop: 8 }}>Reading subscriptions&hellip;</p>
            </div>
        );
    }

    return (
        <div className="s-root" style={{ background: 'var(--s-panel)', border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-lg)', padding: '26px 28px 34px' }}>
            {/* ⚠️ Nota lama berkata "flow_* collections do not exist yet". Itu sudah TIDAK
                betul — langganan sebenar memang wujud sejak halaman bayar dilancarkan.
                Yang belum ada ialah SAMBUNGAN antara skrin ini dan data itu. Ayat mesti
                jujur, kalau tidak orang membaca skrin ini sebagai keadaan sebenar. */}
            {/* Jalur ini mesti sentiasa menyatakan keadaan SEBENAR. Versi lama
                berkata "nombor di bawah adalah rekaan" — itu sudah tidak betul
                sejak 26 Ogos, dan jalur yang berbohong lebih buruk daripada
                tiada jalur langsung. */}
            {sel !== 'session-access' && sel !== 'hosted-jobs' && (ralat ? (
                <div className="s-sample" style={{ background: 'var(--s-stop-soft)', color: 'var(--s-stop)' }}>
                    <b>Tidak dapat membaca data.</b> {ralat}
                </div>
            ) : (
                ringkasanRalat ? (
                    <div className="s-sample" style={{ background: 'var(--s-warn-soft)', color: 'var(--s-warn)' }}>
                        <b>Gmail details could not be loaded</b> ({ringkasanRalat}). Status below may show &ldquo;Needs a Gmail&rdquo; for customers who already have one.
                    </div>
                ) : null
            ))}

            <div style={{ marginBottom: 24 }}>
                <h1 className="s-title">Flow Ultra</h1>
                <p className="s-subtitle">Manage your Google subscriptions, family sharing accounts and tenants.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '214px minmax(0,1fr)', gap: 30, alignItems: 'start' }}>
                {rail}
                <main style={{ minWidth: 0 }}>
                    {sel === 'session-access' && canManageSessions ? <FlowSessionPilotTab />
                        : sel === 'hosted-jobs' && canManageSessions ? <FlowHostedJobsTab />
                        : sel === 'overview' ? overview
                        : sel === 'customers' ? customersScreen
                        : sel === 'orders' ? <FlowOrdersScreen orders={data?.orders || []} customers={customers} produk={produk}
                            onOpenCustomer={(c) => { setSel('customers'); setCustFor(c); }} />
                        : sel === 'products' ? <FlowProductsScreen produk={produk} ralatMuat={produkRalat}
                            subsCount={(id) => customers.filter((c) => produkUntuk(c, produk)?.id === id).length}
                            onSaved={(m) => { toast(m); muatSemula(); }} />
                            : sel === 'calendar' ? calendarScreen
                                : sel === 'accounts' ? accountsScreen
                                    /* Pagar: `sel` boleh kekal menuding ke blok
                                       yang sudah tiada selepas data dimuat semula. */
                                    : parents[sel] ? renderParent(parents[sel], sel)
                                        : overview}
                </main>
            </div>

            {/* ═══ Modal kredential ═══ */}
            {credFor && (
                <div className="fu-modal-bg" onClick={() => setCredFor(null)}>
                    <div className="fu-modal fu-modal--sm" onClick={(e) => e.stopPropagation()}>
                        <div className="fu-modal__h">
                            <div>
                                <p className="s-cardname">Account credentials</p>
                                <p className="s-dim">{credFor.email}</p>
                            </div>
                            {/* Pagar PIN ini TIDAK menyemak apa-apa lagi. Lencana ini kekal
                                sehingga semakan sebenar dibina — supaya sesiapa yang menyambung
                                vault kemudian hari tidak tersilap sangka pagar sudah berfungsi. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <Badge tone="warn">No PIN check yet</Badge>
                                <IconBtn label="Close" icon={<X size={15} />} onClick={() => setCredFor(null)} />
                            </div>
                        </div>
                        <div className="fu-modal__b">
                            {!credUnlocked ? (
                                <>
                                    <p className="s-meta">Enter your admin PIN to reveal the password.</p>
                                    <input className="s-inp" maxLength={6} placeholder="••••••"
                                        style={{ textAlign: 'center', letterSpacing: '.5em', fontFamily: 'var(--s-mono)' }} />
                                    <p className="s-dim">Not wired yet — the PIN check and the encrypted vault are Phase&nbsp;1 backend work.</p>
                                    <Btn onClick={() => setCredUnlocked(true)} style={{ width: '100%' }}>Verify</Btn>
                                </>
                            ) : (
                                <>
                                    {credRows.map((r) => (
                                        <div key={r.label} className="s-row" style={{ border: '1px solid var(--s-line)', borderRadius: 10, padding: '11px 14px' }}>
                                            <span className="s-dim" style={{ width: 76, flex: 'none' }}>{r.label}</span>
                                            <span className="s-row__main s-num" style={{ fontSize: 13.5, color: 'var(--s-ink)' }}>
                                                {revealed[r.label] ? r.value : '••••••••••'}
                                            </span>
                                            <Btn variant="ghost" size="sm"
                                                onClick={() => setRevealed((prev) => ({ ...prev, [r.label]: !prev[r.label] }))}>
                                                {revealed[r.label] ? 'Hide' : 'Reveal'}
                                            </Btn>
                                        </div>
                                    ))}
                                    <p className="s-dim">Values shown here are placeholders. Every reveal will be logged once the vault is built.</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Modal tukar password ═══ */}
            {rotateFor && (
                <div className="fu-modal-bg" onClick={() => setRotateFor(null)}>
                    <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="fu-modal__h">
                            <div>
                                <p className="s-cardname">Change account password</p>
                                <p className="s-dim">{rotateFor.email}</p>
                            </div>
                            <IconBtn label="Close" icon={<X size={15} />} onClick={() => setRotateFor(null)} />
                        </div>
                        <div className="fu-modal__b">
                            <div style={{ border: '1px solid var(--s-line-strong)', borderRadius: 10, padding: '12px 14px', background: 'var(--s-warn-soft)' }}>
                                <p style={{ margin: 0, fontSize: 13, color: 'var(--s-warn)' }}>
                                    Change the password once a tenant leaves. Otherwise the former tenant can still sign in.
                                </p>
                            </div>
                            <div>
                                <p className="s-kv__lab">Required checklist</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {[
                                        'Password changed in Google',
                                        'Pressed “Sign out of all devices”',
                                        'Pressed “Regenerate backup codes” — old codes revoked',
                                        "Removed any authenticator or phone number that isn't mine",
                                        'This account is still a member of the parent family group',
                                    ].map((label, i) => (
                                        <label key={i} className="s-chk">
                                            <input type="checkbox" checked={rotateChecks[i]}
                                                onChange={() => setRotateChecks((prev) => prev.map((v, k) => (k === i ? !v : v)))} />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p className="s-kv__lab">New password</p>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input className="s-inp" style={{ fontFamily: 'var(--s-mono)' }} value={rotatePw}
                                        placeholder="Type one or press Generate"
                                        onChange={(e) => setRotatePw(e.target.value)} />
                                    <Btn variant="ghost"
                                        onClick={() => setRotatePw(`Fl0w-${Math.random().toString(36).slice(2, 8)}+`)}>Generate</Btn>
                                </div>
                            </div>
                        </div>
                        <div className="fu-modal__f">
                            <Btn variant="quiet" onClick={() => setRotateFor(null)}>Close</Btn>
                            <Btn disabled={!rotateChecks.every(Boolean)}
                                onClick={() => { setRotateFor(null); toast('Not wired yet — saving the new password is Phase 1 backend work.'); }}>
                                Save &amp; prepare messages
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Modal slot ═══ */}
            {slotRef && (
                <div className="fu-modal-bg" onClick={() => setSlotRef(null)}>
                    <div className="fu-modal fu-modal--sm" onClick={(e) => e.stopPropagation()}>
                        <div className="fu-modal__h">
                            <div>
                                <p className="s-cardname">
                                    {slotData === null ? 'Empty slot' : slotData === 'rotate' ? 'Released slot' : 'Tenant'}
                                </p>
                                <p className="s-dim">
                                    {shortMail(slotAcc.email)} · slot {slotRef.slotIdx + 1} · block {slotParent.no}
                                </p>
                            </div>
                            <IconBtn label="Close" icon={<X size={15} />} onClick={() => setSlotRef(null)} />
                        </div>
                        <div className="fu-modal__b">
                            {slotData === null && (
                                <>
                                    <p className="s-meta">This slot is ready to sell.</p>
                                    <div><p className="s-kv__lab">WhatsApp number</p><input className="s-inp" placeholder="011-2345 6789" /></div>
                                    <div className="s-kv">
                                        <div><p className="s-kv__lab">Price (RM)</p><input className="s-inp" defaultValue="100" /></div>
                                        <div><p className="s-kv__lab">Expires on</p><input className="s-inp" defaultValue="2026-09-06" /></div>
                                    </div>
                                </>
                            )}
                            {slotData === 'rotate' && (
                                <p className="s-meta">This slot has been released. Change the account password so the former tenant can no longer sign in.</p>
                            )}
                            {isTenant(slotData) && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--s-line)', borderRadius: 10, padding: '12px 14px' }}>
                                        <span className="s-ic"><KeyRound size={15} strokeWidth={1.9} /></span>
                                        <div>
                                            <p className="s-cardname s-num" style={{ fontSize: 14, color: 'var(--s-ink)' }}>{slotData.p}</p>
                                            <p className="s-dim">{slotAcc.priv ? 'Private · RM300' : 'Sharing · RM100'}/month</p>
                                        </div>
                                    </div>
                                    <div className="s-kv">
                                        <div>
                                            <p className="s-kv__lab">Expiry</p>
                                            <div className="s-kv__box">
                                                {slotData.d < 0 ? `${plural(Math.abs(slotData.d), 'day')} overdue` : `${plural(slotData.d, 'day')} left`}
                                            </div>
                                        </div>
                                        <div><p className="s-kv__lab">Payment method</p><div className="s-kv__box">Maybank (manual)</div></div>
                                    </div>
                                    <div><p className="s-kv__lab">Notes</p><textarea className="s-inp" rows={2} placeholder="e.g. friend rate, paid half upfront" /></div>
                                </>
                            )}
                        </div>
                        <div className="fu-modal__f">
                            <Btn variant="quiet" onClick={() => setSlotRef(null)}>Close</Btn>
                            <Btn onClick={() => { setSlotRef(null); toast('Not wired yet — Phase 1 backend work.'); }}>
                                {slotData === null ? 'Place tenant' : 'Extend 1 month'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ Popup Edit Subscription (PLAN_FLOW_PRODUK.md) ═══
                Dibaca semula dari senarai terkini setiap render — selepas Save email /
                notes / tukar type, popup menunjuk data baharu tanpa ditutup. */}
            {custFor && (() => {
                const c = customers.find((x) => x.id === custFor.id && jenisRekod(x) === jenisRekod(custFor)) || custFor;
                return (
                    <FlowSubscriptionModal c={c} st={STAGE[c.stage] || STAGE.active} produk={produk}
                        lain={langgananLain(c, customers)} onOpenLain={(x) => setCustFor(x)}
                        onClose={() => setCustFor(null)}
                        onWhatsApp={bukaWa}
                        onChangeDate={(x) => { setCustFor(null); setTarikhFor(x); }}
                        onGoSessionAccess={canManageSessions ? () => { setCustFor(null); setSel('session-access'); } : null}
                        onChanged={(msg, muat, tutup) => { if (msg) toast(msg); if (muat) muatSemula(); if (tutup) setCustFor(null); }} />
                );
            })()}

            {/* ═══ Modal: tambah pelanggan tangan ═══ */}
            {tambahBuka && (
                <div className="fu-modal-bg" onClick={() => !sedangSimpan && setTambahBuka(false)}>
                    <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="fu-modal__h">
                            <div>
                                <p className="s-cardname">Add customer</p>
                                <p className="s-dim">Someone who paid outside the system — a transfer, or an older sale from your spreadsheet.</p>
                            </div>
                            <IconBtn label="Close" icon={<X size={15} />} onClick={() => setTambahBuka(false)} />
                        </div>

                        <div className="fu-modal__b">
                            {produkAktif.length > 0 && (
                                <div>
                                    <p className="s-kv__lab">Product</p>
                                    <select className="s-inp" value={borang.produkId}
                                        onChange={(e) => setBorang((b) => isiDariProduk(b, e.target.value))}>
                                        {produkAktif.map((x) => (
                                            <option key={x.id} value={x.id}>{x.name} — RM{x.priceSen / 100} · {x.days} days · Type {x.jenis}</option>
                                        ))}
                                    </select>
                                    <p className="s-kv__hint">Price, expiry and credit limit below are filled from the product. You can still change them for this customer.</p>
                                </div>
                            )}
                            <div style={produkAktif.length > 0 ? { display: 'none' } : undefined}>
                                <p className="s-kv__lab">Type</p>
                                <select className="s-inp" value={borang.jenis} onChange={(e) => ubahBorang('jenis', e.target.value)}>
                                    <option value="1">Type 1 — Lite LP only (shared family manager)</option>
                                    <option value="2">Type 2 — own Gmail with credits (family member)</option>
                                </select>
                                {borang.jenis === '2' && (
                                    <p className="s-kv__hint">Type 2 needs the Gmail given to the customer, and their SESAAT account must be found — by email, or by a WhatsApp number saved in that account. Access ends on the family&rsquo;s Flow Ultra renewal date, and the full credit limit must be used before then. Store that Gmail&rsquo;s password and 2FA key in the vault separately.</p>
                                )}
                            </div>

                            <div>
                                <p className="s-kv__lab">WhatsApp number or SESAAT email</p>
                                <input className="s-inp" value={borang.hubungan}
                                    placeholder="0123456789 or name@gmail.com"
                                    onChange={(e) => ubahBorang('hubungan', e.target.value)} />
                                <p className="s-kv__hint">Type either one. The customer is matched to their SESAAT account, and the other detail is filled in from that account.</p>
                            </div>

                            <div>
                                <p className="s-kv__lab">Name <span className="s-dim">(optional)</span></p>
                                <input className="s-inp" value={borang.name}
                                    onChange={(e) => ubahBorang('name', e.target.value)} />
                            </div>

                            <div className="s-kv">
                                <div>
                                    <p className="s-kv__lab">Subscription starts</p>
                                    <input className="s-inp" type="date" value={borang.mula}
                                        onChange={(e) => ubahBorang('mula', e.target.value)} />
                                </div>
                                <div>
                                    <p className="s-kv__lab">{borang.jenis === '2' ? 'Family renews on (access ends)' : 'Expires on'}</p>
                                    <input className="s-inp" type="date" value={borang.tamat}
                                        onChange={(e) => ubahBorang('tamat', e.target.value)} />
                                </div>
                                <div>
                                    <p className="s-kv__lab">Paid on</p>
                                    <input className="s-inp" type="date" value={borang.bayar}
                                        onChange={(e) => ubahBorang('bayar', e.target.value)} />
                                </div>
                                <div>
                                    <p className="s-kv__lab">Price (RM)</p>
                                    <input className="s-inp" value={borang.harga} inputMode="decimal"
                                        onChange={(e) => ubahBorang('harga', e.target.value)} />
                                </div>
                                {borang.jenis === '2' && (
                                    <div>
                                        <p className="s-kv__lab">Credit limit</p>
                                        <input className="s-inp" value={borang.had} inputMode="numeric"
                                            onChange={(e) => ubahBorang('had', e.target.value)} />
                                    </div>
                                )}
                            </div>
                            <p className="s-kv__hint" style={{ marginTop: -6 }}>The payment date is what the admin Dashboard counts as this month&rsquo;s sale.</p>

                            <div>
                                <p className="s-kv__lab">{borang.jenis === '2' ? 'Gmail given to this customer' : <>Google account <span className="s-dim">(optional)</span></>}</p>
                                <input className="s-inp" value={borang.accountEmail}
                                    placeholder="jualinsta5@gmail.com"
                                    onChange={(e) => ubahBorang('accountEmail', e.target.value)} />
                                <p className="s-kv__hint">Type it as it appears in your spreadsheet. It becomes a real link once the accounts are imported.</p>
                            </div>

                            <div>
                                <p className="s-kv__lab">Note <span className="s-dim">(optional)</span></p>
                                <input className="s-inp" value={borang.note}
                                    onChange={(e) => ubahBorang('note', e.target.value)} />
                            </div>

                            <label className="s-chk">
                                <input type="checkbox" checked={borang.hantar}
                                    onChange={(e) => ubahBorang('hantar', e.target.checked)} />
                                <span>
                                    Access details already sent
                                    <br />
                                    <span className="s-dim" style={{ fontSize: 12.5 }}>
                                        Leave this off and they show up under &ldquo;Ready to send&rdquo; so you do not forget them.
                                    </span>
                                </span>
                            </label>

                            {borangRalat && (
                                <p style={{ color: 'var(--s-stop)', fontSize: 13, margin: 0 }}>{borangRalat}</p>
                            )}
                            {/* Rekod produk yang sama sudah ada: lazimnya ini RENEWAL yang dibayar manual, bukan
                                langganan kedua. Tawarkan laluan betul dahulu — baris kedua hanya bila memang mahu. */}
                            {sahPendua && (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Btn size="sm" onClick={bukaRenewSedia} disabled={sedangSimpan}>Record as a renewal instead</Btn>
                                    <span className="s-dim" style={{ fontSize: 12.5 }}>
                                        Adds the days to their existing subscription (same Gmail and projects) and records this payment as a receipt.
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="fu-modal__f">
                            <Btn variant="ghost" onClick={() => setTambahBuka(false)} disabled={sedangSimpan}>Cancel</Btn>
                            <Btn onClick={hantarBorang} disabled={sedangSimpan}>
                                {sedangSimpan ? 'Saving…' : sahPendua ? 'Add as a second subscription' : 'Add customer'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* Pilihan diambil daripada SEMUA pelanggan, bukan custList — tanda boleh
                merentas tapisan/carian (lihat nota tanda-semua). */}
            {extendBuka && (
                <FlowExtendDaysModal produk={produk}
                    customers={customers.filter((c) => picked.has(c.id))}
                    onClose={() => setExtendBuka(false)}
                    onDone={(r) => { if (r?.ok) muatSemula(); }} />
            )}

            {tarikhFor && (
                <FlowExtendDaysModal mode="set" produk={produk}
                    customers={[tarikhFor]}
                    onClose={() => setTarikhFor(null)}
                    onDone={(r) => { if (r?.ok) muatSemula(); }} />
            )}

            {/* "Record as a renewal instead" dari borang Add customer (2026-10-14): langganan sedia ada,
                Paid renewal sudah ditick, RM + tarikh bayar dibawa dari borang. */}
            {renewDari && (
                <FlowExtendDaysModal mode="paid" produk={produk}
                    customers={renewDari.customers} bayaranAwal={renewDari.bayaranAwal}
                    onClose={() => setRenewDari(null)}
                    onDone={(r) => { if (r?.ok) muatSemula(); }} />
            )}

            {toastMsg && <div className="s-toast">{toastMsg}</div>}
        </div>
    );
}
